// SCR-STAFF-885 — الگو و اسناد تکرارشونده (US-ACC-022؛ AP-ACC-03): الگوی ردیف‌های ثابت سند با دوره تکرار ماهانه یا فصلی و بازه اعتبار.
// الگوی سررسیدشده را Job پایان روز (`accounting.day_close`) به **پیش‌نویس** تبدیل می‌کند، نه سند قطعی؛ پس هر سند تکرارشونده هم از همان
// گردش تأیید US-ACC-023 می‌گذرد. دکمه «اجرای سررسیدها» همان کار Job را دستی و Idempotent انجام می‌دهد.
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
  unwrap,
  type AccJournalTemplateRequest,
  type AccJournalTemplateView,
  type AccRecurrencePeriod,
} from '@phoenix/api-client';
import { useAccess, useIdempotencyKey, useTenantSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import {
  ConfirmDialog,
  DataTable,
  ErrorState,
  faDigits,
  FormDialog,
  JalaliDate,
  JalaliDatePickerRHF,
  ListPage,
  LoadingState,
  type DataTableColumn,
} from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { accKeys, Amount, num, SCR_STAFF_885, useAccBranches, useBranchName } from './common';

const PERIODS: readonly Exclude<AccRecurrencePeriod, null>[] = ['Monthly', 'Quarterly'];

interface LineForm {
  accountId: string;
  detail1Id: string;
  debit: string;
  credit: string;
  description: string;
}

const EMPTY_LINE: LineForm = { accountId: '', detail1Id: '', debit: '', credit: '', description: '' };

function toRials(value: string): number {
  const digits = value.replace(/[^\d۰-۹٠-٩]/g, '').replace(/[۰-۹٠-٩]/g, (char) => {
    const code = char.charCodeAt(0);
    return String(code >= 0x0660 && code <= 0x0669 ? code - 0x0660 : code - 0x06f0);
  });
  return digits ? Number(digits) : 0;
}

export function JournalTemplatesPage() {
  const { t } = useTranslation('acc');
  const { tenantApi, session } = useTenantSession();
  const access = useAccess();
  const queryClient = useQueryClient();
  const key = useIdempotencyKey();
  const branchName = useBranchName();
  const canManage = access.accessMode === 'Full' && access.can('acc.journal.manual.create');
  const templates = useQuery({
    queryKey: accKeys.journalTemplates(session?.tenantId),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/accounting/journal-templates')),
  });
  const [adding, setAdding] = useState(false);
  const [deactivating, setDeactivating] = useState<AccJournalTemplateView | null>(null);
  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['accounting', session?.tenantId] });
  const deactivate = useMutation({
    mutationFn: (id: string) =>
      unwrap(
        tenantApi.POST('/api/v1/accounting/journal-templates/{id}:deactivate', {
          params: { path: { id } },
        }),
      ),
    onSuccess: () => {
      setDeactivating(null);
      refresh();
    },
  });
  const runDue = useMutation({
    mutationFn: () => unwrap(tenantApi.POST('/api/v1/accounting/journal-templates/:run-due', {})),
    onSuccess: refresh,
  });
  const columns: DataTableColumn<AccJournalTemplateView>[] = [
    { id: 'name', headerKey: 'acc:template.name', cell: (row) => row.name },
    { id: 'description', headerKey: 'acc:journal.description', cell: (row) => row.description },
    { id: 'branch', headerKey: 'acc:common.branch', cell: (row) => branchName(row.branchId) },
    {
      id: 'period',
      headerKey: 'acc:template.period',
      cell: (row) => t(`acc:recurrence.${row.period ?? 'None'}`),
    },
    {
      id: 'range',
      headerKey: 'acc:template.range',
      cell: (row) =>
        row.startDate ? (
          <span>
            <JalaliDate value={row.startDate} /> — {row.endDate ? <JalaliDate value={row.endDate} /> : '…'}
          </span>
        ) : (
          '—'
        ),
    },
    { id: 'total', headerKey: 'acc:common.total', cell: (row) => <Amount money={row.total} /> },
    {
      id: 'last_run',
      headerKey: 'acc:template.last_run',
      cell: (row) =>
        row.lastRunDate ? (
          <span data-testid="acc-template-last-run">
            <JalaliDate value={row.lastRunDate} />
          </span>
        ) : (
          '—'
        ),
    },
    {
      id: 'lines',
      headerKey: 'acc:template.lines',
      cell: (row) => faDigits(row.lines.length),
    },
    {
      id: 'actions',
      headerKey: 'acc:common.actions',
      cell: (row) =>
        canManage && row.isActive ? (
          <Button
            size="small"
            color="warning"
            onClick={() => setDeactivating(row)}
            data-testid="acc-template-deactivate"
          >
            {t('acc:template.deactivate')}
          </Button>
        ) : (
          t('acc:common.inactive')
        ),
    },
  ];
  return (
    <ListPage
      screenId={SCR_STAFF_885}
      titleKey="acc:template.title"
      subtitleKey="acc:template.subtitle"
      actions={
        canManage ? (
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              onClick={() => runDue.mutate()}
              disabled={runDue.isPending}
              data-testid="acc-template-run-due"
            >
              {t('acc:template.run_due')}
            </Button>
            <Button variant="contained" onClick={() => setAdding(true)} data-testid="acc-template-add">
              {t('acc:template.add')}
            </Button>
          </Stack>
        ) : undefined
      }
    >
      {runDue.error ? <ErrorState error={runDue.error} compact /> : null}
      {runDue.data ? (
        <Alert severity="success" sx={{ mb: 2 }} data-testid="acc-template-run-result">
          {t('acc:template.run_result', {
            templates: faDigits(num(runDue.data.templates)),
            drafts: faDigits(num(runDue.data.drafts)),
          })}
        </Alert>
      ) : null}
      <DataTable
        captionKey="acc:template.title"
        columns={columns}
        rows={templates.data?.items}
        getRowId={(row) => row.id}
        isLoading={templates.isPending}
        error={templates.error}
        onRetry={() => void templates.refetch()}
        emptyTitleKey="acc:template.empty"
      />
      {adding ? (
        <TemplateForm
          idempotency={key}
          onDone={() => {
            setAdding(false);
            refresh();
          }}
          onClose={() => setAdding(false)}
        />
      ) : null}
      <ConfirmDialog
        open={deactivating !== null}
        titleKey="acc:template.deactivate"
        messageKey="acc:template.deactivate_message"
        confirmKey="acc:template.deactivate"
        destructive
        busy={deactivate.isPending}
        error={deactivate.error}
        onConfirm={() => deactivating && deactivate.mutate(deactivating.id)}
        onCancel={() => setDeactivating(null)}
      />
    </ListPage>
  );
}

function TemplateForm({
  idempotency,
  onDone,
  onClose,
}: {
  idempotency: ReturnType<typeof useIdempotencyKey>;
  onDone: () => void;
  onClose: () => void;
}) {
  const { t } = useTranslation('acc');
  const { tenantApi, session } = useTenantSession();
  const branches = useAccBranches();
  const accounts = useQuery({
    queryKey: accKeys.accounts(session?.tenantId),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/accounting/chart/accounts')),
  });
  const details = useQuery({
    queryKey: accKeys.details(session?.tenantId),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/accounting/detail-accounts')),
  });
  const { control, watch } = useForm<{ startDate: string | null; endDate: string | null }>({
    defaultValues: { startDate: null, endDate: null },
  });
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [branchId, setBranchId] = useState(branches[0]?.branchId ?? '');
  const [period, setPeriod] = useState<Exclude<AccRecurrencePeriod, null> | ''>('Monthly');
  const [lines, setLines] = useState<LineForm[]>([{ ...EMPTY_LINE }, { ...EMPTY_LINE }]);
  const leafAccounts = (accounts.data?.items ?? []).filter(
    (account) =>
      account.isActive && !(accounts.data?.items ?? []).some((child) => child.parentId === account.id),
  );
  const detailItems = (details.data?.items ?? []).filter((detail) => detail.isActive);
  const debit = lines.reduce((sum, line) => sum + toRials(line.debit), 0);
  const credit = lines.reduce((sum, line) => sum + toRials(line.credit), 0);
  const save = useMutation({
    mutationFn: () => {
      const body: AccJournalTemplateRequest = {
        name: name.trim(),
        description: description.trim(),
        branchId,
        period: period || null,
        startDate: watch('startDate'),
        endDate: watch('endDate'),
        lines: lines
          .filter((line) => line.accountId !== '')
          .map((line) => ({
            accountId: line.accountId,
            detail1Id: line.detail1Id || null,
            detail2Id: null,
            detail3Id: null,
            debit: toRials(line.debit) > 0 ? { amount: toRials(line.debit), currency: 'IRR' } : null,
            credit: toRials(line.credit) > 0 ? { amount: toRials(line.credit), currency: 'IRR' } : null,
            description: line.description.trim() || null,
          })),
      };
      return unwrap(
        tenantApi.POST('/api/v1/accounting/journal-templates', {
          body,
          headers: { 'Idempotency-Key': idempotency.current() },
        }),
      );
    },
    onSettled: (_data, error) => idempotency.settle(error ?? undefined),
    onSuccess: onDone,
  });
  const setLine = (index: number, patch: Partial<LineForm>) =>
    setLines((current) => current.map((line, position) => (position === index ? { ...line, ...patch } : line)));
  return (
    <FormDialog
      open
      titleKey="acc:template.add"
      maxWidth="lg"
      isDirty={name !== '' || lines.some((line) => line.accountId !== '')}
      isSubmitting={save.isPending}
      submitError={save.error}
      onSubmit={() => save.mutate()}
      onClose={onClose}
    >
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
        <TextField
          label={t('acc:template.name')}
          value={name}
          required
          onChange={(event) => setName(event.target.value)}
          slotProps={{ htmlInput: { 'data-testid': 'acc-template-name' } }}
        />
        <TextField
          label={t('acc:journal.description')}
          value={description}
          required
          fullWidth
          onChange={(event) => setDescription(event.target.value)}
          slotProps={{ htmlInput: { 'data-testid': 'acc-template-description' } }}
        />
        <TextField
          select
          label={t('acc:common.branch')}
          value={branchId}
          required
          onChange={(event) => setBranchId(event.target.value)}
          sx={{ minWidth: 180 }}
        >
          {branches.map((branch) => (
            <MenuItem key={branch.branchId} value={branch.branchId}>
              {branch.name}
            </MenuItem>
          ))}
        </TextField>
      </Stack>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
        <TextField
          select
          label={t('acc:template.period')}
          value={period}
          onChange={(event) => setPeriod(event.target.value as Exclude<AccRecurrencePeriod, null> | '')}
          sx={{ minWidth: 180 }}
          slotProps={{ htmlInput: { 'data-testid': 'acc-template-period' } }}
        >
          <MenuItem value="">{t('acc:recurrence.None')}</MenuItem>
          {PERIODS.map((value) => (
            <MenuItem key={value} value={value}>
              {t(`acc:recurrence.${value}`)}
            </MenuItem>
          ))}
        </TextField>
        <JalaliDatePickerRHF control={control} name="startDate" labelKey="acc:template.start" />
        <JalaliDatePickerRHF control={control} name="endDate" labelKey="acc:template.end" />
      </Stack>
      {accounts.isPending || details.isPending ? <LoadingState rows={2} /> : null}
      <Paper variant="outlined" sx={{ p: 1 }}>
        <Table size="small" aria-label={t('acc:template.lines')}>
          <TableHead>
            <TableRow>
              <TableCell>{t('acc:journal.account')}</TableCell>
              <TableCell>{t('acc:journal.details')}</TableCell>
              <TableCell align="right">{t('acc:journal.debit')}</TableCell>
              <TableCell align="right">{t('acc:journal.credit')}</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {lines.map((line, index) => (
              <TableRow key={index} data-testid="acc-template-form-line">
                <TableCell sx={{ minWidth: 220 }}>
                  <TextField
                    select
                    size="small"
                    fullWidth
                    value={line.accountId}
                    onChange={(event) => setLine(index, { accountId: event.target.value })}
                    slotProps={{ htmlInput: { 'data-testid': `acc-template-account-${index}` } }}
                  >
                    <MenuItem value="">{t('acc:common.none')}</MenuItem>
                    {leafAccounts.map((account) => (
                      <MenuItem key={account.id} value={account.id}>
                        <bdi dir="ltr">{account.code}</bdi>&nbsp;{account.title}
                      </MenuItem>
                    ))}
                  </TextField>
                </TableCell>
                <TableCell sx={{ minWidth: 200 }}>
                  <TextField
                    select
                    size="small"
                    fullWidth
                    value={line.detail1Id}
                    onChange={(event) => setLine(index, { detail1Id: event.target.value })}
                  >
                    <MenuItem value="">{t('acc:common.none')}</MenuItem>
                    {detailItems.map((detail) => (
                      <MenuItem key={detail.id} value={detail.id}>
                        <bdi dir="ltr">{detail.code}</bdi>&nbsp;{detail.title}
                      </MenuItem>
                    ))}
                  </TextField>
                </TableCell>
                <TableCell align="right">
                  <TextField
                    size="small"
                    value={line.debit}
                    onChange={(event) => setLine(index, { debit: event.target.value, credit: '' })}
                    slotProps={{
                      htmlInput: {
                        dir: 'ltr',
                        inputMode: 'numeric',
                        'data-testid': `acc-template-debit-${index}`,
                      },
                    }}
                  />
                </TableCell>
                <TableCell align="right">
                  <TextField
                    size="small"
                    value={line.credit}
                    onChange={(event) => setLine(index, { credit: event.target.value, debit: '' })}
                    slotProps={{
                      htmlInput: {
                        dir: 'ltr',
                        inputMode: 'numeric',
                        'data-testid': `acc-template-credit-${index}`,
                      },
                    }}
                  />
                </TableCell>
                <TableCell>
                  <IconButton
                    size="small"
                    aria-label={t('acc:manual.remove_line')}
                    disabled={lines.length <= 2}
                    onClick={() => setLines((current) => current.filter((_, position) => position !== index))}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 1 }}>
          <Button
            size="small"
            onClick={() => setLines((current) => [...current, { ...EMPTY_LINE }])}
            data-testid="acc-template-add-line"
          >
            {t('acc:manual.add_line')}
          </Button>
          <Typography variant="body2" data-testid="acc-template-total-debit" data-amount={debit}>
            {t('acc:manual.total_debit')}: {faDigits(debit)}
          </Typography>
          <Typography variant="body2" data-testid="acc-template-total-credit" data-amount={credit}>
            {t('acc:manual.total_credit')}: {faDigits(credit)}
          </Typography>
        </Stack>
      </Paper>
      {debit !== credit ? (
        <Alert severity="warning" data-testid="acc-template-unbalanced">
          {t('acc:manual.unbalanced')}
        </Alert>
      ) : null}
    </FormDialog>
  );
}
